import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { SideNavigation as CloudscapeSideNavigation, Badge, Hotspot } from '@cloudscape-design/components';
import { useAuth } from '../context/AuthContext.js';
import { useRoleMatrix, toCanonicalRole } from '../context/RoleMatrixContext';
import { apiFetch } from '../auth/apiClient';
import useReminderView from '../pages/reminders/useReminderView';
import useReminderAttentionCount from './useReminderAttentionCount';

const SideNavigation = ({ showTutorialHotspots = false, notificationCount = 0, refreshNotifications, notificationsLoading = false }) => {
  const { currentRole, isAuthenticated } = useAuth();
  const pruneSections = (items = []) =>
    items.filter(item => {
      if (!item) return false;
      if (item.type !== 'section') return true;
      const childItems = Array.isArray(item.items) ? item.items.filter(Boolean) : [];
      return childItems.length > 0;
    });

  const history = useHistory();
  const { roleMatrix, isLoading: roleMatrixLoading } = useRoleMatrix();
  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      const raw = sessionStorage.getItem('sideNavExpanded');
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr : []);
    } catch { return new Set(); }
  });

  useEffect(() => {
    try { sessionStorage.setItem('sideNavExpanded', JSON.stringify(Array.from(expandedSections))); } catch {}
  }, [expandedSections]);

  useEffect(() => {
    if (!showTutorialHotspots) return;
    setExpandedSections(prev => {
      if (prev.has('Support')) return prev;
      const next = new Set(prev);
      next.add('Support');
      return next;
    });
  }, [showTutorialHotspots]);

  const [contactCount, setContactCount] = useState(null);
  const [messageCount, setMessageCount] = useState(null);

  useEffect(() => {
    let isCancelled = false;
    const controller = new AbortController();

    const fetchCount = async status => {
      const params = new URLSearchParams({ pageSize: '1' });
      if (status) params.append('status', status);
      const response = await apiFetch(`/api/admin/contact-messages?${params.toString()}`, { method: 'GET', signal: controller.signal });
      if (!response.ok) {
        const error = new Error('Failed to load contact messages');
        error.status = response.status;
        throw error;
      }
      const data = await response.json();
      return Number(data?.total ?? 0);
    };

    async function loadCounts() {
      try {
        const [newCount, inProgressCount] = await Promise.all([fetchCount('new'), fetchCount('in-progress')]);
        if (!isCancelled) setContactCount(newCount + inProgressCount);
      } catch (error) {
        if (!isCancelled) {
          console.error('[SideNavigation] contact count fetch failed', error);
          setContactCount(null);
        }
      }
    }

    loadCounts();
    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    async function loadMessageCount() {
      try {
        const resp = await apiFetch('/api/me/staff-messages/counts', { method: 'GET', signal: controller.signal });
        if (!resp.ok) throw new Error(`status ${resp.status}`);
        const json = await resp.json();
        const unread = Number(json?.inbox?.unread ?? 0);
        if (!cancelled) setMessageCount(unread);
      } catch (err) {
        if (!cancelled) {
          console.error('[SideNavigation] message count fetch failed', err);
          setMessageCount(null);
        }
      }
    }
    loadMessageCount();
    const onRefresh = () => loadMessageCount();
    window.addEventListener('staff-messages:refresh', onRefresh);
    return () => {
      cancelled = true;
      controller.abort();
      window.removeEventListener('staff-messages:refresh', onRefresh);
    };
  }, []);

  const allNavItems = [
    {
      type: 'section',
      text: 'Intake and Assessment',
      items: [
        { type: 'link', text: 'Manual Application Intake', href: '/iset/applications/intake' },
        { type: 'link', text: 'Application Assessment', href: '/case-assignment-dashboard' },
        { type: 'link', text: 'Minimum Service Level', href: '/case-management/register-interaction' },
      ],
    },
    {
      type: 'section',
      text: 'Case Management',
      items: [
        { type: 'link', text: 'Case Management', href: '/iset/cases' },
        { type: 'link', text: 'My Case Queue', href: '/case-management' },
        { type: 'link', text: 'Job Bank Search', href: '/job-bank-search' },
      ],
    },
    {
      type: 'section',
      text: 'Budgets and Finance',
      items: [
        { type: 'link', text: 'Finance Overview', href: '/finance/overview' },
        { type: 'link', text: 'Budgets', href: '/finance/budgets' },
        { type: 'link', text: 'Budget Planning', href: '/finance/budgets/planning' },
        { type: 'link', text: 'Salaries', href: '/finance/salaries' },
        { type: 'link', text: 'Expenses', href: '/finance/expenses' },
        { type: 'link', text: 'Allocations & Transfers', href: '/finance/allocations' },
        { type: 'link', text: 'Payments', href: '/finance/payments' },
        { type: 'link', text: 'Reconciliation', href: '/finance/reconciliation' },
        { type: 'link', text: 'Financial Reports', href: '/finance/reports' },
        { type: 'link', text: 'Monitoring & Evidence', href: '/finance/monitoring' },
        { type: 'link', text: 'Forecasting & Scenarios', href: '/finance/forecasting' },
        { type: 'link', text: 'Finance Settings', href: '/finance/settings' },
      ],
    },
    {
      type: 'section',
      text: 'ILMP Submissions',
      items: [
        { type: 'link', text: 'Overview', href: '/esdc/overview' },
        { type: 'link', text: 'ILMP Exports', href: '/esdc/participants' },
        { type: 'link', text: 'Reporting', href: '/esdc/reporting' },
      ],
    },
    {
      type: 'section',
      text: 'Edit Digital Forms',
      items: [
        { type: 'link', text: 'Manage Intake Steps', href: '/manage-components' },
        { type: 'link', text: 'Manage Workflows', href: '/manage-workflows' },
      ],
    },
    {
      type: 'section',
      text: 'Reporting',
      defaultExpanded: false,
      items: [
        { type: 'link', text: 'Regional Snapshot', href: '/reporting/regional-snapshot' },
        { type: 'link', text: 'Activity Report', href: '/reporting/activity-report' },
        { type: 'link', text: 'Data and Results', href: '/reporting/data-and-results' },
        { type: 'link', text: 'Operations Report', href: '/reporting/operations-and-timeliness' },
        { type: 'link', text: 'Minimum Service Level', href: '/reporting/minimum-service-level' },
      ],
    },
    {
      type: 'section',
      text: 'ISET Administration',
      items: [
        { type: 'link', text: 'NWAC Hub Management', href: '/nwac-hub-management' },
      ],
    },
    {
      type: 'section',
      text: 'Configuration',
      defaultExpanded: false,
      items: [
        { type: 'link', text: 'User Management', href: '/user-management-dashboard' },
        { type: 'link', text: 'Release Management', href: '/release-management-dashboard' },
        { type: 'link', text: 'Notification Settings', href: '/manage-notifications' },
        { type: 'link', text: 'Template Editor', href: '/template-editor' },
        { type: 'link', text: 'Language Settings', href: '/language-settings-dashboard' },
        { type: 'link', text: 'Event Logging', href: '/configuration/events' },
        { type: 'link', text: 'Applicant Watchlist', href: '/configuration/applicant-watchlist' },
        { type: 'link', text: 'Configuration Settings', href: '/configuration-settings' },
        { type: 'link', text: 'Client Batch Import', href: '/iset/imports/client-files' },
        { type: 'link', text: 'Query Editor', href: '/configuration/query-editor' },
        { type: 'link', text: 'File Upload Config', href: '/admin/upload-config' },
      ],
    },
    {
      type: 'section',
      text: 'Security',
      defaultExpanded: false,
      items: [
        { type: 'link', text: 'Audit and Logs', href: '/audit-logs-dashboard' },
        { type: 'link', text: 'Security Settings', href: '/manage-security-options' },
        { type: 'link', text: 'Access Control', href: '/access-control' },
      ],
    },
    {
      type: 'section',
      text: 'Support',
      defaultExpanded: false,
      items: [
        { type: 'link', text: 'Guidance', href: '/documentation' },
        { type: 'link', text: 'Bugs and Change Requests', href: '/support/bugs-change-requests' },
        {
          type: 'link',
          text: 'Tutorials',
          href: '/tutorials-dashboard',
          info: showTutorialHotspots ? (
            <Hotspot hotspotId="intro-tutorials-link" direction="right" />
          ) : undefined,
        },
        { type: 'link', text: 'Help and Support', href: '/help-support-dashboard' },
      ],
    },
  ];

  const notificationsFooterItem = useMemo(() => {
    const item = {
      type: 'link',
      id: 'footer-notifications',
      text: 'Notifications',
      href: '#refresh-notifications',
      external: false,
    };

    item.info = (
      <span style={{ display: 'inline-flex', pointerEvents: 'none' }} aria-hidden="true">
        <Badge color={notificationCount > 0 ? 'red' : 'grey'}>{notificationCount}</Badge>
      </span>
    );

    return item;
  }, [notificationCount]);

  const messagesFooterItem = useMemo(() => {
    const placeholderCount = messageCount ?? 0;
    return {
      type: 'link',
      id: 'footer-messages',
      text: 'Messages',
      href: '/messages',
      external: false,
      info: (
        <span style={{ display: 'inline-flex', pointerEvents: 'none' }} aria-hidden="true">
          <Badge color={placeholderCount > 0 ? 'blue' : 'grey'}>{placeholderCount}</Badge>
        </span>
      ),
    };
  }, [messageCount]);

  function isAllowed(href, roleValue) {
    if (!href) return true;
    const allowed = roleMatrix?.routes?.[href];
    const canonicalRole = toCanonicalRole(roleValue);
    if (allowed) return allowed.includes(canonicalRole);
    if (roleMatrix?.default === 'deny') return false;
    return true;
  }

  const remindersAllowed = isAuthenticated && !roleMatrixLoading && Boolean(roleMatrix)
    && isAllowed('/reminders', currentRole?.value || currentRole);
  const { view: reminderView, identityKey: reminderIdentity, ready: reminderViewReady, options: reminderViewOptions } = useReminderView();
  const reminderCount = useReminderAttentionCount(remindersAllowed && reminderViewReady, reminderIdentity, reminderView);

  function filterNavItemsForRole(role, signedOut) {
    if (signedOut) {
      const supportSection = allNavItems.find(section => section.text === 'Support');
      return supportSection ? pruneSections([{ ...supportSection, defaultExpanded: true }]) : [];
    }

    const roleValue = role?.value || role;
    const canonicalRole = toCanonicalRole(roleValue);

    const filteredSections = allNavItems.map(section => {
      if (!section.items) return section;
      return {
        ...section,
        items: section.items.filter(item => isAllowed(item.href, canonicalRole)),
      };
    });

    const footerItems = [];
    const ensureDivider = () => {
      if (!footerItems.some(item => item?.type === 'divider')) {
        footerItems.push({ type: 'divider' });
      }
    };

    if (isAllowed('/contact-communications', canonicalRole)) {
      ensureDivider();
      const contactLink = {
        type: 'link',
        href: '/contact-communications',
        text: 'Contact Communications',
        id: 'footer-contact-communications',
      };
      if (contactCount !== null && contactCount >= 0) {
        contactLink.info = (
          <span style={{ display: 'inline-flex', pointerEvents: 'none' }} aria-hidden="true">
            <Badge color={contactCount > 0 ? 'blue' : 'grey'}>
              {contactCount}
            </Badge>
          </span>
        );
      }
      footerItems.push(contactLink);
    }

    ensureDivider();
    const existingContactIndex = footerItems.findIndex(item => item?.href === '/contact-communications');
    const insertAt = existingContactIndex >= 0 ? existingContactIndex + 1 : footerItems.length;
    footerItems.splice(insertAt, 0, notificationsFooterItem);

    if (!roleMatrixLoading && isAllowed('/reminders', canonicalRole)) {
      const notificationsIndex = footerItems.findIndex(item => item?.id === 'footer-notifications');
      footerItems.splice(notificationsIndex, 0, {
        type: 'link',
        id: 'footer-reminders',
        text: 'Reminders',
        href: '/reminders',
        info: reminderCount === null ? undefined : (
          <span style={{ display: 'inline-flex', pointerEvents: 'none' }}
            title={`Outstanding reminders due today or overdue — ${reminderViewOptions.find(option => option.value === reminderView)?.label || ''}`}
            aria-label={`${reminderCount} outstanding reminders due today or overdue`}>
            <Badge color={reminderCount > 0 ? 'red' : 'grey'}>{reminderCount}</Badge>
          </span>
        ),
      });
    }

    if (isAllowed('/messages', canonicalRole)) {
      ensureDivider();
      const notificationsIndex = footerItems.findIndex(item => item?.id === 'footer-notifications');
      const insertAt = notificationsIndex >= 0 ? notificationsIndex + 1 : footerItems.length;
      footerItems.splice(insertAt, 0, messagesFooterItem);
    }

    return [...pruneSections(filteredSections), ...footerItems];
  }

  const filteredNavItems = filterNavItemsForRole(currentRole, !isAuthenticated);

  const itemsWithExpandState = useMemo(() => {
    const apply = (items) => items.map(item => {
      if (item?.type === 'section') {
        const key = item.text || item.href || JSON.stringify(item);
        return {
          ...item,
          defaultExpanded: expandedSections.has(key),
          items: item.items ? apply(item.items) : item.items,
        };
      }
      return item;
    });
    return apply(filteredNavItems);
  }, [filteredNavItems, expandedSections]);

  return (
    <Hotspot hotspotId="intro-side-navigation" direction="right">
      <CloudscapeSideNavigation
        header={{
          href: '/',
          text: 'Homepage',
        }}
        items={itemsWithExpandState}
        onChange={(e) => {
          const detail = e?.detail;
          const item = detail?.item;
          if (item?.type === 'section') {
            const key = item.text || item.href || JSON.stringify(item);
            setExpandedSections(prev => {
              const next = new Set(prev);
              if (detail?.expanded) next.add(key); else next.delete(key);
              return next;
            });
          }
        }}
        onFollow={(e) => {
          const detail = e?.detail;
          const item = detail?.item;
          if (detail?.href === '/case-management/register-interaction') {
            e.preventDefault();
            window.dispatchEvent(new CustomEvent('minimum-service:open'));
            return;
          }

          if (item?.id === 'footer-notifications') {
            e.preventDefault();
            if (notificationsLoading) {
              return;
            }
            if (typeof refreshNotifications === 'function') {
              try {
                const result = refreshNotifications();
                if (result && typeof result.catch === 'function') {
                  result.catch(err => console.error('[SideNavigation] notification refresh failed', err));
                }
              } catch (err) {
                console.error('[SideNavigation] notification refresh failed', err);
              }
            }
            return;
          }

          if (detail && detail.href && !detail.external) {
            e.preventDefault();
            history.push(detail.href);
          }
        }}
      />
    </Hotspot>
  );
};

export default SideNavigation;
